import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface IgnoreAtLaunchDialogProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const IgnoreAtLaunchDialog: React.FC<IgnoreAtLaunchDialogProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onIgnoreConfirmation = () => {
		if (teaser !== null) ignoreTeaser(teaser);
	};

	const { mutate: ignoreTeaser } = useMutation<DataRoomTeaser, unknown, DataRoomTeaser>(
		teaserToIgnore =>
			getResponseData(
				api.patch<DataRoomTeaser>(CerebroApiEndpoints.Teasers.IgnoreAtLaunch(dataRoomGuid, teaserToIgnore.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, teaserToIgnore) => {
				const message = teaserToIgnore.ignoreAtLaunch ? 'Teaser Included.' : 'Teaser Ignored.';
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar(message, { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem ignoring the teaser.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={teaser !== null} onClose={close}>
			<DialogTitle>Confirm Action</DialogTitle>
			<DialogContent>
				{teaser && !teaser.ignoreAtLaunch && (
					<DialogContentText>
						Are you sure you want to ignore this teaser for {teaser?.firstName} {teaser?.lastName} at{' '}
						{teaser?.bankName}? By selecting yes this teaser will not be sent when launching the deal.
					</DialogContentText>
				)}
				{teaser && teaser.ignoreAtLaunch && (
					<DialogContentText>
						Are you sure you want to include this teaser for {teaser?.firstName} {teaser?.lastName} at{' '}
						{teaser?.bankName}? By selecting yes this teaser will be sent when launching the deal.
					</DialogContentText>
				)}
				{!teaser && <DialogContentText></DialogContentText>}
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onIgnoreConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
